import { type FormEvent, useMemo, useState } from 'react';

type UploadResult = {
  key: string;
  size: number;
  etag: string;
  uploaded: string;
};

function endpointFor(key: string): string {
  return `/api/r2-test/${key.split('/').map(encodeURIComponent).join('/')}`;
}

async function errorMessage(response: Response): Promise<string> {
  const fallback = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json() as { error?: string };
    return body.error || fallback;
  } catch {
    return fallback;
  }
}

export function R2TestPage() {
  const [file, setFile] = useState<File | null>(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState<'upload' | 'download' | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState('');
  const endpoint = useMemo(() => endpointFor(key.trim()), [key]);

  async function upload(event: FormEvent) {
    event.preventDefault();
    if (!file || !key.trim()) return;
    setBusy('upload');
    setError('');
    setResult(null);
    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'content-type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!response.ok) throw new Error(await errorMessage(response));
      setResult(await response.json() as UploadResult);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Upload failed.');
    } finally {
      setBusy(null);
    }
  }

  async function download() {
    if (!key.trim()) return;
    setBusy('download');
    setError('');
    try {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(await errorMessage(response));
      const blobUrl = URL.createObjectURL(await response.blob());
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = key.trim().split('/').pop() || 'r2-download';
      anchor.click();
      URL.revokeObjectURL(blobUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Download failed.');
    } finally {
      setBusy(null);
    }
  }

  return <div className="content-page page-width r2-test-page">
    <div className="page-heading">
      <div>
        <p className="eyebrow">Temporary proof of concept</p>
        <h1>R2 transfer test</h1>
        <p>Upload an object to R2 and download it back through a Cloudflare Pages Function proxy. This test surface and its API are intentionally disposable.</p>
      </div>
    </div>

    <div className="notice warn">Do not use this endpoint for sensitive or production data. It has no authentication and objects are isolated only by a test prefix.</div>

    <form className="panel r2-test-panel" onSubmit={upload}>
      <label>
        <span>Object key</span>
        <input
          value={key}
          onChange={(event) => setKey(event.target.value)}
          placeholder="examples/hello.txt"
          maxLength={512}
          required
        />
      </label>
      <label>
        <span>File to upload</span>
        <input
          type="file"
          onChange={(event) => {
            const nextFile = event.target.files?.[0] ?? null;
            setFile(nextFile);
            if (nextFile && !key) setKey(nextFile.name);
          }}
          required
        />
      </label>
      <div className="r2-test-actions">
        <button className="button primary" disabled={busy !== null || !file || !key.trim()}>
          {busy === 'upload' ? 'Uploading…' : 'Upload to R2'}
        </button>
        <button className="button secondary" type="button" onClick={download} disabled={busy !== null || !key.trim()}>
          {busy === 'download' ? 'Downloading…' : 'Download from R2'}
        </button>
      </div>
      {key.trim() && <p className="r2-endpoint">Function endpoint: <code>{endpoint}</code></p>}
    </form>

    {result && <div className="notice good">
      Uploaded <strong>{result.key}</strong> ({result.size.toLocaleString()} bytes). ETag: <code>{result.etag}</code>
    </div>}
    {error && <div className="notice bad" role="alert">{error}</div>}
  </div>;
}
